import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../../components/AppIcon';
import { tiendaDb } from '../../lib/supabase';
import EpelsaBascula from './components/EpelsaBascula';
import EpelsaConfiguracion from './components/EpelsaConfiguracion';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const RED = '#F87171';
const ORANGE = '#FB923C';
const BATCH_SIZE = 200;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtDate = (ts) => ts ? new Date(ts)?.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const fmtTime = (ts) => ts ? new Date(ts)?.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' }) : '—';
const todayMadrid = () => new Date()?.toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });

const normalizePago = (metodo_codigo, metodo_nombre) => {
  const code = (metodo_codigo || '')?.toLowerCase();
  const name = (metodo_nombre || '')?.toLowerCase();
  if (code === 't.credito' || code === 'credito' || name?.includes('tarjeta') || name?.includes('credito') || name?.includes('crédito')) return 'Tarjeta';
  if (code === 'efectivo' || name?.includes('efectivo') || name?.includes('cash')) return 'Efectivo';
  if (code === 'bizum' || name?.includes('bizum')) return 'Bizum';
  return metodo_nombre || metodo_codigo || 'Otros';
};

// ─── PRESET RANGES ────────────────────────────────────────────────────────────
const getPresetRange = (preset) => {
  const now = new Date();
  const tz = 'Europe/Madrid';
  const todayStr = now?.toLocaleDateString('sv-SE', { timeZone: tz });
  const yesterday = new Date(now); yesterday?.setDate(yesterday?.getDate() - 1);
  const yesterdayStr = yesterday?.toLocaleDateString('sv-SE', { timeZone: tz });
  const dayOfWeek = now?.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' });
  const daysMap = { Sun: 6, Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5 };
  const daysFromMonday = daysMap?.[dayOfWeek] ?? 0;
  const weekStart = new Date(now); weekStart?.setDate(weekStart?.getDate() - daysFromMonday);
  const weekStartStr = weekStart?.toLocaleDateString('sv-SE', { timeZone: tz });
  const prevWeekEnd = new Date(weekStart); prevWeekEnd?.setDate(prevWeekEnd?.getDate() - 1);
  const prevWeekStart = new Date(prevWeekEnd); prevWeekStart?.setDate(prevWeekStart?.getDate() - 6);
  const monthStartStr = `${todayStr?.slice(0, 7)}-01`;
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthStartStr = prevMonth?.toLocaleDateString('sv-SE', { timeZone: tz })?.slice(0, 7) + '-01';
  const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  const prevMonthEndStr = prevMonthEnd?.toLocaleDateString('sv-SE', { timeZone: tz });
  switch (preset) {
    case 'hoy': return { desde: todayStr, hasta: todayStr };
    case 'ayer': return { desde: yesterdayStr, hasta: yesterdayStr };
    case 'semana': return { desde: weekStartStr, hasta: todayStr };
    case 'semana_anterior': return { desde: prevWeekStart?.toLocaleDateString('sv-SE', { timeZone: tz }), hasta: prevWeekEnd?.toLocaleDateString('sv-SE', { timeZone: tz }) };
    case 'mes': return { desde: monthStartStr, hasta: todayStr };
    case 'mes_anterior': return { desde: prevMonthStartStr, hasta: prevMonthEndStr };
    default: return { desde: todayStr, hasta: todayStr };
  }
};

// ─── EPELSA VENTAS SERVICE ────────────────────────────────────────────────────
const epelsaVentasService = {
  // Get tickets with server-side filters and pagination
  getTickets: async ({ desde, hasta, search, vendedor, formaPago, tipo, importeMin, importeMax, pageSize, page, sortBy, sortDir }) => {
    let query = tiendaDb?.from('epelsa_tickets')
      ?.select('id, numero_ticket, fecha, fecha_operacion, total, base_imponible, iva_importe, vendedor_nombre, vendedor_codigo, lineas_numero, forma_pago, estado, numero_factura, cliente_nombre, cliente_codigo, balanza_codigo, scale_codigo, counter_codigo, counter_nombre, factura_simplificada', { count: 'exact' });

    if (desde) query = query?.gte('fecha_operacion', desde);
    if (hasta) query = query?.lte('fecha_operacion', hasta);
    if (importeMin !== '' && importeMin != null) query = query?.gte('total', parseFloat(importeMin));
    if (importeMax !== '' && importeMax != null) query = query?.lte('total', parseFloat(importeMax));
    if (tipo === 'ventas') query = query?.gte('total', 0);
    if (tipo === 'devoluciones') query = query?.lt('total', 0);
    if (vendedor) query = query?.or(`vendedor_nombre.ilike.%${vendedor}%,vendedor_codigo.ilike.%${vendedor}%`);
    if (search) {
      const s = search?.trim();
      if (/^\d+$/?.test(s)) {
        query = query?.or(`numero_ticket.ilike.%${s}%,numero_factura.ilike.%${s}%`);
      } else {
        query = query?.or(`numero_ticket.ilike.%${s}%,numero_factura.ilike.%${s}%,vendedor_nombre.ilike.%${s}%,cliente_nombre.ilike.%${s}%`);
      }
    }

    const sortCol = sortBy || 'fecha';
    query = query?.order(sortCol, { ascending: sortDir === 'asc' });
    const from = (page - 1) * pageSize;
    query = query?.range(from, from + pageSize - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    // If filtering by forma_pago, we need to filter via pagos table
    // For now, return tickets and let KPIs handle pago filtering
    return { tickets: data || [], total: count || 0 };
  },

  // Get tickets filtered by payment method (requires pagos join)
  getTicketsByPago: async ({ desde, hasta, formaPago, pageSize, page }) => {
    // Get ticket IDs that have the specified payment method
    let pagoQuery = tiendaDb?.from('epelsa_ticket_pagos')?.select('ticket_id, metodo_codigo, metodo_nombre');
    const { data: pagos, error: pErr } = await pagoQuery;
    if (pErr) throw pErr;

    const matchingIds = new Set();
    for (const p of pagos || []) {
      const label = normalizePago(p?.metodo_codigo, p?.metodo_nombre);
      if (label === formaPago) matchingIds?.add(p?.ticket_id);
    }
    return Array.from(matchingIds);
  },

  // Get KPIs for a date range — uses server-side RPC to bypass 1000-row limit.
  // tienda.get_epelsa_kpis performs SUM/COUNT over the FULL dataset in PostgreSQL.
  getKPIs: async (desde, hasta) => {
    if (!desde || !hasta) return { ventas: 0, nTickets: 0, ticketMedio: 0, efectivo: 0, tarjeta: 0, bizum: 0, otros: 0, diasConVentas: 0 };
    const { data, error } = await tiendaDb?.rpc('get_epelsa_kpis', {
      p_desde: desde,
      p_hasta: hasta,
    });
    if (error) throw error;
    const row = data?.[0] || {};
    const ventas = parseFloat(row?.total_ventas) || 0;
    const nTickets = parseInt(row?.n_tickets) || 0;
    const ticketMedio = nTickets > 0 ? ventas / nTickets : 0;
    return {
      ventas,
      nTickets,
      ticketMedio,
      efectivo: parseFloat(row?.efectivo) || 0,
      tarjeta: parseFloat(row?.tarjeta) || 0,
      bizum: parseFloat(row?.bizum) || 0,
      otros: parseFloat(row?.otros) || 0,
      diasConVentas: parseInt(row?.dias_con_ventas) || 0,
    };
  },

  // Get artículos (líneas) with server-side filters and pagination
  getLineas: async ({ desde, hasta, search, vendedor, familia, pageSize, page, sortBy, sortDir }) => {
    // First get ticket IDs in date range
    let ticketQuery = tiendaDb?.from('epelsa_tickets')?.select('id, fecha, fecha_operacion, vendedor_nombre, vendedor_codigo');
    if (desde) ticketQuery = ticketQuery?.gte('fecha_operacion', desde);
    if (hasta) ticketQuery = ticketQuery?.lte('fecha_operacion', hasta);
    if (vendedor) ticketQuery = ticketQuery?.or(`vendedor_nombre.ilike.%${vendedor}%,vendedor_codigo.ilike.%${vendedor}%`);

    const { data: tickets, error: tErr } = await ticketQuery;
    if (tErr) throw tErr;
    if (!tickets?.length) return { lineas: [], total: 0 };

    const ticketMap = {};
    for (const t of tickets) ticketMap[t.id] = t;
    const ticketIds = tickets?.map((t) => t?.id);

    // Fetch lineas in batches with filters
    const allLineas = [];
    for (let i = 0; i < ticketIds?.length; i += BATCH_SIZE) {
      const batch = ticketIds?.slice(i, i + BATCH_SIZE);
      let lQuery = tiendaDb?.from('epelsa_ticket_lineas')
        ?.select('id, ticket_id, articulo_codigo, descripcion, familia_codigo, cantidad, peso, precio_unitario, iva_porcentaje, total, sale_form, fecha_linea');
      lQuery = lQuery?.in('ticket_id', batch);
      if (familia) lQuery = lQuery?.eq('familia_codigo', familia);
      if (search) lQuery = lQuery?.or(`descripcion.ilike.%${search}%,articulo_codigo.ilike.%${search}%`);
      const { data: l } = await lQuery;
      if (l) allLineas?.push(...l);
    }

    // Attach ticket info
    const enriched = allLineas?.map((l) => ({
      ...l,
      ticket: ticketMap?.[l?.ticket_id] || null,
    }));

    // Sort
    const sortCol = sortBy || 'fecha_linea';
    enriched?.sort((a, b) => {
      let va = a?.[sortCol] || a?.ticket?.fecha || '';
      let vb = b?.[sortCol] || b?.ticket?.fecha || '';
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });

    const total = enriched?.length;
    const from = (page - 1) * pageSize;
    return { lineas: enriched?.slice(from, from + pageSize), total };
  },

  // Get ticket detail with pagos and lineas
  getTicketDetail: async (ticketId) => {
    const [ticketResult, pagosResult, lineasResult] = await Promise.all([
      tiendaDb?.from('epelsa_tickets')?.select('*')?.eq('id', ticketId)?.single(),
      tiendaDb?.from('epelsa_ticket_pagos')?.select('*')?.eq('ticket_id', ticketId),
      tiendaDb?.from('epelsa_ticket_lineas')?.select('*')?.eq('ticket_id', ticketId),
    ]);
    const ticketData = ticketResult?.data;
    const pagosData = pagosResult?.data;
    const lineasData = lineasResult?.data;
    return { ticket: ticketData, pagos: pagosData || [], lineas: lineasData || [] };
  },

  // Get distinct vendedores
  getVendedores: async () => {
    const { data } = await tiendaDb?.from('epelsa_tickets')?.select('vendedor_nombre, vendedor_codigo')?.not('vendedor_nombre', 'is', null)?.limit(200);
    const seen = new Set();
    return (data || [])?.filter((v) => { const k = v?.vendedor_nombre || v?.vendedor_codigo; if (seen?.has(k)) return false; seen?.add(k); return true; });
  },

  // Get distinct familias
  getFamilias: async () => {
    const { data } = await tiendaDb?.from('epelsa_ticket_lineas')?.select('familia_codigo')?.not('familia_codigo', 'is', null)?.limit(500);
    const seen = new Set();
    return (data || [])?.map((f) => f?.familia_codigo)?.filter((f) => { if (seen?.has(f)) return false; seen?.add(f); return true; })?.sort();
  },
};

// ─── TICKET DETAIL MODAL ──────────────────────────────────────────────────────
const TicketDetailModal = ({ ticketId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    epelsaVentasService?.getTicketDetail(ticketId)?.then(setDetail)?.catch(() => setDetail(null))?.finally(() => setLoading(false));
  }, [ticketId]);

  const t = detail?.ticket;
  const isDevolucion = t && parseFloat(t?.total) < 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-xl rounded-2xl max-h-[92vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4" style={{ background: '#141414', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <h3 className="font-oranienbaum font-bold text-white text-lg">Ticket #{t?.numero_ticket || '...'}</h3>
            <p className="font-oranienbaum text-white/40 text-xs">{t ? `${fmtDate(t?.fecha)} · ${fmtTime(t?.fecha)}` : '...'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" /></div>
          ) : !detail ? (
            <p className="font-oranienbaum text-white/40 text-sm text-center py-8">Error al cargar el ticket</p>
          ) : (
            <>
              {/* Info */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Vendedor', value: t?.vendedor_nombre || t?.vendedor_codigo || '—' },
                  { label: 'Caja/Balanza', value: t?.counter_nombre || t?.balanza_codigo || t?.scale_codigo || '—' },
                  { label: 'Cliente', value: t?.cliente_nombre || '—' },
                  { label: 'Factura', value: t?.numero_factura || '—' },
                  { label: 'Base imponible', value: fmtEur(parseFloat(t?.base_imponible)) },
                  { label: 'IVA', value: fmtEur(parseFloat(t?.iva_importe)) },
                  { label: 'Total', value: fmtEur(parseFloat(t?.total)), color: isDevolucion ? ORANGE : ACCENT },
                  { label: 'Tipo', value: isDevolucion ? 'Devolución' : 'Venta', color: isDevolucion ? ORANGE : ACCENT },
                ]?.map((item, i) => (
                  <div key={i} className="p-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="font-oranienbaum text-white/40 text-xs">{item?.label}</p>
                    <p className="font-oranienbaum font-bold text-sm" style={{ color: item?.color || 'rgba(255,255,255,0.85)' }}>{item?.value}</p>
                  </div>
                ))}
              </div>
              {/* Pagos */}
              {detail?.pagos?.length > 0 && (
                <div>
                  <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-2">Formas de pago</p>
                  <div className="space-y-1">
                    {detail?.pagos?.map((p, i) => (
                      <div key={i} className="flex justify-between px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <span className="font-oranienbaum text-white/60 text-sm">{normalizePago(p?.metodo_codigo, p?.metodo_nombre)}</span>
                        <span className="font-oranienbaum font-bold text-sm" style={{ color: ACCENT }}>{fmtEur(parseFloat(p?.importe_entregado))}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Líneas */}
              {detail?.lineas?.length > 0 && (
                <div>
                  <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-2">Líneas ({detail?.lineas?.length})</p>
                  <div className="space-y-1">
                    {detail?.lineas?.map((l, i) => (
                      <div key={i} className="px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-oranienbaum text-white/80 text-sm flex-1 truncate">{l?.descripcion || l?.articulo_codigo || '—'}</span>
                          <span className="font-oranienbaum font-bold text-sm ml-2" style={{ color: GOLD }}>{fmtEur(parseFloat(l?.total))}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {l?.articulo_codigo && <span className="font-oranienbaum text-white/30 text-xs">Cód: {l?.articulo_codigo}</span>}
                          {l?.familia_codigo && <span className="font-oranienbaum text-white/30 text-xs">Fam: {l?.familia_codigo}</span>}
                          {l?.peso > 0 && <span className="font-oranienbaum text-white/40 text-xs">{fmt(l?.peso, 3)} kg</span>}
                          {l?.cantidad > 0 && <span className="font-oranienbaum text-white/40 text-xs">×{fmt(l?.cantidad, 0)}</span>}
                          {l?.precio_unitario > 0 && <span className="font-oranienbaum text-white/40 text-xs">{fmtEur(parseFloat(l?.precio_unitario))}/u</span>}
                          {l?.iva_porcentaje > 0 && <span className="font-oranienbaum text-white/30 text-xs">IVA {l?.iva_porcentaje}%</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'ventas', label: 'Ventas', icon: 'TrendingUp' },
  { id: 'bascula', label: 'Báscula Epelsa', icon: 'Scale' },
  { id: 'config-epelsa', label: 'Configuración Epelsa', icon: 'Settings' },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TiendaVentas = () => {
  const [activeTab, setActiveTab] = useState('ventas');
  const [vistaActiva, setVistaActiva] = useState('tickets'); // 'tickets' | 'articulos'

  // Filters
  const [preset, setPreset] = useState('hoy');
  const [desde, setDesde] = useState(() => getPresetRange('hoy')?.desde);
  const [hasta, setHasta] = useState(() => getPresetRange('hoy')?.hasta);
  const [search, setSearch] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [tipo, setTipo] = useState(''); // '' | 'ventas' | 'devoluciones'
  const [importeMin, setImporteMin] = useState('');
  const [importeMax, setImporteMax] = useState('');
  const [familia, setFamilia] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('fecha');
  const [sortDir, setSortDir] = useState('desc');

  // Data
  const [tickets, setTickets] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [totalLineas, setTotalLineas] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailTicketId, setDetailTicketId] = useState(null);

  // Load filter options once
  useEffect(() => {
    epelsaVentasService?.getVendedores()?.then(setVendedores)?.catch(() => {});
    epelsaVentasService?.getFamilias()?.then(setFamilias)?.catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    if (activeTab !== 'ventas') return;
    try {
      setLoading(true); setError(null);
      if (vistaActiva === 'tickets') {
        const [result, kpiData] = await Promise.all([
          epelsaVentasService?.getTickets({ desde, hasta, search, vendedor, formaPago, tipo, importeMin, importeMax, pageSize, page, sortBy, sortDir }),
          epelsaVentasService?.getKPIs(desde, hasta),
        ]);
        setTickets(result?.tickets);
        setTotalTickets(result?.total);
        setKpis(kpiData);
      } else {
        const [result, kpiData] = await Promise.all([
          epelsaVentasService?.getLineas({ desde, hasta, search, vendedor, familia, pageSize, page, sortBy, sortDir }),
          epelsaVentasService?.getKPIs(desde, hasta),
        ]);
        setLineas(result?.lineas);
        setTotalLineas(result?.total);
        setKpis(kpiData);
      }
    } catch (e) { setError(e?.message || 'Error al cargar datos'); }
    finally { setLoading(false); }
  }, [activeTab, vistaActiva, desde, hasta, search, vendedor, formaPago, tipo, importeMin, importeMax, familia, pageSize, page, sortBy, sortDir]);

  useEffect(() => { loadData(); }, [loadData]);

  const applyPreset = (p) => {
    setPreset(p); const r = getPresetRange(p);
    setDesde(r?.desde); setHasta(r?.hasta); setPage(1);
  };

  const clearFilters = () => {
    setSearch(''); setVendedor(''); setFormaPago(''); setTipo('');
    setImporteMin(''); setImporteMax(''); setFamilia('');
    applyPreset('hoy'); setPage(1);
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
    setPage(1);
  };

  const activeFilters = [
    preset && { key: 'preset', label: preset?.replace('_', ' ') },
    search && { key: 'search', label: `"${search}"` },
    vendedor && { key: 'vendedor', label: `Vendedor: ${vendedor}` },
    formaPago && { key: 'formaPago', label: formaPago },
    tipo && { key: 'tipo', label: tipo === 'ventas' ? 'Solo ventas' : 'Solo devoluciones' },
    (importeMin || importeMax) && { key: 'importe', label: `${importeMin || '0'} – ${importeMax || '∞'} €` },
    familia && { key: 'familia', label: `Familia: ${familia}` },
  ]?.filter(Boolean);

  const removeFilter = (key) => {
    if (key === 'preset') applyPreset('hoy');
    if (key === 'search') setSearch('');
    if (key === 'vendedor') setVendedor('');
    if (key === 'formaPago') setFormaPago('');
    if (key === 'tipo') setTipo('');
    if (key === 'importe') { setImporteMin(''); setImporteMax(''); }
    if (key === 'familia') setFamilia('');
    setPage(1);
  };

  const totalPages = Math.ceil((vistaActiva === 'tickets' ? totalTickets : totalLineas) / pageSize);
  const totalCount = vistaActiva === 'tickets' ? totalTickets : totalLineas;

  const PRESETS = [
    { key: 'hoy', label: 'Hoy' }, { key: 'ayer', label: 'Ayer' },
    { key: 'semana', label: 'Semana' }, { key: 'semana_anterior', label: 'Semana anterior' },
    { key: 'mes', label: 'Mes' }, { key: 'mes_anterior', label: 'Mes anterior' },
  ];

  const SortIcon = ({ col }) => (
    <Icon name={sortBy === col ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={11} className="inline ml-1 opacity-50" />
  );

  const kpiCards = [
    { label: 'Ventas', value: kpis ? (kpis?.ventas >= 0 ? fmtEur(kpis?.ventas) : fmtEur(kpis?.ventas)) : '—', icon: 'TrendingUp', color: ACCENT },
    { label: 'Tickets', value: kpis?.nTickets ?? '—', icon: 'Receipt', color: GOLD },
    { label: 'Ticket medio', value: kpis ? fmtEur(kpis?.ticketMedio) : '—', icon: 'ShoppingBag', color: BLUE },
    { label: 'Efectivo', value: kpis ? fmtEur(kpis?.efectivo) : '—', icon: 'Banknote', color: ACCENT },
    { label: 'Tarjeta', value: kpis ? fmtEur(kpis?.tarjeta) : '—', icon: 'CreditCard', color: PURPLE },
    { label: 'Bizum', value: kpis ? fmtEur(kpis?.bizum) : '—', icon: 'Smartphone', color: ORANGE },
  ];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {detailTicketId && <TicketDetailModal ticketId={detailTicketId} onClose={() => setDetailTicketId(null)} />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="TrendingUp" size={18} style={{ color: ACCENT }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Ventas</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Tickets Epelsa · Datos reales</p>
        </div>
        {activeTab === 'ventas' && (
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={13} />Actualizar
          </button>
        )}
      </div>
      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl mb-5 overflow-x-auto" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        {TABS?.map(tab => (
          <button key={tab?.id} onClick={() => { setActiveTab(tab?.id); setPage(1); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-oranienbaum whitespace-nowrap transition-all flex-shrink-0"
            style={activeTab === tab?.id
              ? { background: `${ACCENT}18`, color: ACCENT, border: `1px solid ${ACCENT}35` }
              : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
            <Icon name={tab?.icon} size={13} />{tab?.label}
          </button>
        ))}
      </div>
      {activeTab === 'bascula' && <EpelsaBascula />}
      {activeTab === 'config-epelsa' && <EpelsaConfiguracion />}
      {activeTab === 'ventas' && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            {kpiCards?.map((kpi, i) => (
              <div key={i} className="rounded-xl p-4 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
                  <Icon name={kpi?.icon} size={15} style={{ color: kpi?.color }} />
                </div>
                <p className="font-oranienbaum font-bold text-white text-xl">{loading ? '...' : kpi?.value}</p>
                <p className="font-oranienbaum text-white/45 text-xs leading-tight">{kpi?.label}</p>
              </div>
            ))}
          </div>

          {/* Vista selector */}
          <div className="flex items-center gap-2 mb-4">
            {['tickets', 'articulos']?.map((v) => (
              <button key={v} onClick={() => { setVistaActiva(v); setPage(1); setSortBy(v === 'tickets' ? 'fecha' : 'fecha_linea'); }}
                className="px-4 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                style={vistaActiva === v
                  ? { background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                {v === 'tickets' ? 'Tickets' : 'Artículos'}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="rounded-2xl p-4 mb-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {/* Quick presets */}
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESETS?.map((p) => (
                <button key={p?.key} onClick={() => applyPreset(p?.key)}
                  className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                  style={{ background: preset === p?.key ? `${GOLD}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${preset === p?.key ? `${GOLD}40` : 'rgba(255,255,255,0.08)'}`, color: preset === p?.key ? GOLD : 'rgba(255,255,255,0.50)' }}>
                  {p?.label}
                </button>
              ))}
            </div>

            {/* Main filter row */}
            <div className="flex flex-wrap gap-3 items-center mb-3">
              <div className="relative flex-1 min-w-48">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="text" placeholder="Buscar ticket, artículo, vendedor..." value={search}
                  onChange={(e) => { setSearch(e?.target?.value); setPage(1); }}
                  className="w-full pl-9 pr-4 py-2 rounded-xl text-xs font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-oranienbaum text-white/40 text-xs">Desde</label>
                <input type="date" value={desde} onChange={(e) => { setPreset(''); setDesde(e?.target?.value); setPage(1); }}
                  className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-oranienbaum text-white/40 text-xs">Hasta</label>
                <input type="date" value={hasta} onChange={(e) => { setPreset(''); setHasta(e?.target?.value); setPage(1); }}
                  className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
              </div>
              <button onClick={() => setShowAdvanced((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                style={{ background: showAdvanced ? `${BLUE}15` : 'rgba(255,255,255,0.04)', border: `1px solid ${showAdvanced ? `${BLUE}30` : 'rgba(255,255,255,0.08)'}`, color: showAdvanced ? BLUE : 'rgba(255,255,255,0.50)' }}>
                <Icon name="SlidersHorizontal" size={12} />Filtros avanzados
              </button>
              <button onClick={clearFilters} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>
                <Icon name="X" size={12} />Limpiar
              </button>
            </div>

            {/* Advanced filters */}
            {showAdvanced && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Vendedor</label>
                  <select value={vendedor} onChange={(e) => { setVendedor(e?.target?.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <option value="">Todos</option>
                    {vendedores?.map((v, i) => <option key={i} value={v?.vendedor_nombre || v?.vendedor_codigo}>{v?.vendedor_nombre || v?.vendedor_codigo}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Forma de pago</label>
                  <select value={formaPago} onChange={(e) => { setFormaPago(e?.target?.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <option value="">Todos</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="Bizum">Bizum</option>
                    <option value="Otros">Otros</option>
                  </select>
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Tipo</label>
                  <select value={tipo} onChange={(e) => { setTipo(e?.target?.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <option value="">Todos</option>
                    <option value="ventas">Solo ventas</option>
                    <option value="devoluciones">Solo devoluciones</option>
                  </select>
                </div>
                {vistaActiva === 'articulos' && (
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">Familia</label>
                    <select value={familia} onChange={(e) => { setFamilia(e?.target?.value); setPage(1); }}
                      className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                      <option value="">Todas</option>
                      {familias?.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Importe mín. (€)</label>
                  <input type="number" step="0.01" value={importeMin} onChange={(e) => { setImporteMin(e?.target?.value); setPage(1); }}
                    placeholder="0" className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Importe máx. (€)</label>
                  <input type="number" step="0.01" value={importeMax} onChange={(e) => { setImporteMax(e?.target?.value); setPage(1); }}
                    placeholder="∞" className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                </div>
                {/* Quick importe ranges */}
                <div className="col-span-2">
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Rangos rápidos</label>
                  <div className="flex flex-wrap gap-1">
                    {[['< 5 €', '', '5'], ['5–10 €', '5', '10'], ['10–20 €', '10', '20'], ['20–50 €', '20', '50'], ['> 50 €', '50', '']]?.map(([label, min, max]) => (
                      <button key={label} onClick={() => { setImporteMin(min); setImporteMax(max); setPage(1); }}
                        className="px-2 py-1 rounded-lg text-xs font-oranienbaum transition-all"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Active filter chips */}
            {activeFilters?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {activeFilters?.map((f) => (
                  <span key={f?.key} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-oranienbaum"
                    style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30`, color: BLUE }}>
                    {f?.label}
                    <button onClick={() => removeFilter(f?.key)} className="hover:text-white transition-colors"><Icon name="X" size={10} /></button>
                  </span>
                ))}
                <button onClick={clearFilters} className="px-2 py-1 rounded-full text-xs font-oranienbaum transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>
                  Limpiar todos
                </button>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
              <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Table */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <span className="font-oranienbaum text-white/50 text-xs">
                {loading ? 'Cargando...' : `${totalCount} ${vistaActiva === 'tickets' ? 'tickets' : 'líneas'} encontrados`}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-oranienbaum text-white/30 text-xs">Por página:</span>
                {[25, 50, 100]?.map((n) => (
                  <button key={n} onClick={() => { setPageSize(n); setPage(1); }}
                    className="px-2 py-1 rounded-lg text-xs font-oranienbaum transition-all"
                    style={{ background: pageSize === n ? `${GOLD}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${pageSize === n ? `${GOLD}40` : 'rgba(255,255,255,0.08)'}`, color: pageSize === n ? GOLD : 'rgba(255,255,255,0.40)' }}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              {/* VISTA TICKETS */}
              {vistaActiva === 'tickets' && (
                <table className="w-full min-w-max">
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {[
                        { col: 'numero_ticket', label: 'Nº Ticket' },
                        { col: 'fecha', label: 'Fecha' },
                        { col: 'fecha', label: 'Hora' },
                        { col: 'cliente_nombre', label: 'Cliente' },
                        { col: null, label: 'Canal' },
                        { col: null, label: 'Origen' },
                        { col: 'vendedor_nombre', label: 'Vendedor' },
                        { col: null, label: 'Forma pago' },
                        { col: 'base_imponible', label: 'Base Imp.' },
                        { col: 'iva_importe', label: 'IVA' },
                        { col: 'total', label: 'Total' },
                        { col: 'estado', label: 'Estado' },
                      ]?.map(({ col, label }) => (
                        <th key={label} className="px-3 py-3 text-left whitespace-nowrap">
                          <button onClick={col ? () => handleSort(col) : undefined}
                            className={`font-oranienbaum text-white/40 text-xs uppercase tracking-wider ${col ? 'hover:text-white/60 transition-colors' : ''}`}>
                            {label}{col && <SortIcon col={col} />}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={12} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
                          <p className="font-oranienbaum text-white/40 text-sm">Cargando tickets Epelsa...</p>
                        </div>
                      </td></tr>
                    ) : tickets?.length === 0 ? (
                      <tr><td colSpan={12} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}15` }}>
                            <Icon name="TrendingUp" size={24} className="text-[#34D399]/40" />
                          </div>
                          <p className="font-oranienbaum text-white/40 text-sm">No hay tickets para los filtros seleccionados</p>
                        </div>
                      </td></tr>
                    ) : tickets?.map((t, i) => {
                      const isDevolucion = parseFloat(t?.total) < 0;
                      return (
                        <tr key={t?.id} onClick={() => setDetailTicketId(t?.id)}
                          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)', cursor: 'pointer' }}
                          className="hover:bg-white/[0.03] transition-colors">
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/85 text-sm">#{t?.numero_ticket}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{fmtDate(t?.fecha)}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{fmtTime(t?.fecha)}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{t?.cliente_nombre || '—'}</span></td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${ACCENT}18`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>Físico</span>
                          </td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${GOLD}15`, color: GOLD, border: `1px solid ${GOLD}30` }}>Epelsa</span>
                          </td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-xs">{t?.vendedor_nombre || t?.vendedor_codigo || '—'}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{t?.forma_pago ? normalizePago(t?.forma_pago, '') : '—'}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{fmtEur(parseFloat(t?.base_imponible))}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-sm">{fmtEur(parseFloat(t?.iva_importe))}</span></td>
                          <td className="px-3 py-3">
                            <span className="font-oranienbaum font-bold text-sm" style={{ color: isDevolucion ? ORANGE : 'white' }}>{fmtEur(parseFloat(t?.total))}</span>
                            {isDevolucion && <span className="ml-1 font-oranienbaum text-xs px-1 py-0.5 rounded" style={{ background: `${ORANGE}15`, color: ORANGE }}>Dev.</span>}
                          </td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${ACCENT}18`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>
                              {isDevolucion ? 'Devolución' : 'Cobrado'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* VISTA ARTÍCULOS */}
              {vistaActiva === 'articulos' && (
                <table className="w-full min-w-max">
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {[
                        { col: 'fecha_linea', label: 'Fecha' },
                        { col: 'fecha_linea', label: 'Hora' },
                        { col: null, label: 'Ticket' },
                        { col: 'articulo_codigo', label: 'Código' },
                        { col: 'descripcion', label: 'Artículo' },
                        { col: 'familia_codigo', label: 'Familia' },
                        { col: 'cantidad', label: 'Cantidad' },
                        { col: 'peso', label: 'Peso' },
                        { col: 'precio_unitario', label: 'Precio' },
                        { col: 'iva_porcentaje', label: 'IVA' },
                        { col: 'total', label: 'Total' },
                        { col: null, label: 'Vendedor' },
                      ]?.map(({ col, label }) => (
                        <th key={label} className="px-3 py-3 text-left whitespace-nowrap">
                          <button onClick={col ? () => handleSort(col) : undefined}
                            className={`font-oranienbaum text-white/40 text-xs uppercase tracking-wider ${col ? 'hover:text-white/60 transition-colors' : ''}`}>
                            {label}{col && <SortIcon col={col} />}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={12} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
                          <p className="font-oranienbaum text-white/40 text-sm">Cargando artículos...</p>
                        </div>
                      </td></tr>
                    ) : lineas?.length === 0 ? (
                      <tr><td colSpan={12} className="px-4 py-16 text-center">
                        <p className="font-oranienbaum text-white/40 text-sm">No hay artículos para los filtros seleccionados</p>
                      </td></tr>
                    ) : lineas?.map((l, i) => (
                      <tr key={l?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                        className="hover:bg-white/[0.03] transition-colors">
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{fmtDate(l?.fecha_linea || l?.ticket?.fecha)}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{fmtTime(l?.fecha_linea || l?.ticket?.fecha)}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">—</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{l?.articulo_codigo || '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/85 text-sm">{l?.descripcion || '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-xs">{l?.familia_codigo || '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{l?.cantidad != null ? fmt(l?.cantidad, 0) : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{l?.peso > 0 ? `${fmt(l?.peso, 3)} kg` : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{l?.precio_unitario > 0 ? fmtEur(parseFloat(l?.precio_unitario)) : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-sm">{l?.iva_porcentaje != null ? `${l?.iva_porcentaje}%` : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum font-bold text-sm" style={{ color: GOLD }}>{fmtEur(parseFloat(l?.total))}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{l?.ticket?.vendedor_nombre || l?.ticket?.vendedor_codigo || '—'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span className="font-oranienbaum text-white/30 text-xs">
                {totalCount === 0 ? '0 – 0 de 0' : `${(page - 1) * pageSize + 1} – ${Math.min(page * pageSize, totalCount)} de ${totalCount}`} resultados
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={page === 1} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronsLeft" size={14} />
                </button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronLeft" size={14} />
                </button>
                <span className="px-3 py-1 rounded-lg font-oranienbaum text-xs" style={{ background: `${ACCENT}15`, color: ACCENT }}>{page} / {totalPages || 1}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronRight" size={14} />
                </button>
                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronsRight" size={14} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TiendaVentas;
